import { z } from 'zod';
import { TimestampsSchema } from './common.js';
// =============================================================================
// Document Schema - PDF metadata with tenant isolation (simplified - no detection)
// =============================================================================
/**
 * Document processing status
 */
export const DocumentStatusEnum = z.enum([
    'UPLOADED', // File is in S3
    'QUEUED', // Sent to SQS for processing
    'PROCESSING', // Python worker is analyzing
    'READY', // Analysis complete, ready for review
    'REVIEWED', // User has reviewed/edited fields
    'DOCUSIGN_QUEUED', // Queued to send to DocuSign (waiting for worker) - DEPRECATED
    'DOCUSIGN_DRAFT', // Draft envelope created, agent reviewing in DocuSign
    'SENT', // Actually sent to DocuSign (envelope sent to recipients)
    'SIGNED', // Fully signed
    'DOCUSIGN_CANCELLED', // Envelope was voided/discarded
    'ERROR', // Processing failed
]);
/**
 * Document type enum
 */
export const DocumentTypeEnum = z.enum([
    'UPLOADED', // User-uploaded document
    'GENERATED_COVER', // Auto-generated cover page
    'GENERATED_SUMMARY', // Auto-generated summary
]);
/**
 * DocuSign envelope status
 */
export const DocuSignEnvelopeStatusEnum = z.enum([
    'created',
    'sent',
    'delivered',
    'completed',
    'declined',
    'voided',
]);
/**
 * S3 storage pointers
 */
export const DocumentStorageSchema = z.object({
    bucket: z.string(),
    originalKey: z.string(), // Original PDF
    annotatedKey: z.string().optional(), // Annotated PDF (after processing)
});
/**
 * DocuSign metadata
 */
export const DocumentDocuSignSchema = z.object({
    envelopeId: z.string().optional(),
    status: DocuSignEnvelopeStatusEnum.optional(),
    sentAt: z.coerce.date().optional(),
    completedAt: z.coerce.date().optional(),
    // Sender view tracking for Embedded Sending
    senderViewGeneratedAt: z.coerce.date().optional(),
    // Error tracking for failed sends
    error: z.string().optional(),
    errorAt: z.coerce.date().optional(),
});
/**
 * Full Document schema (database document)
 */
export const DocumentSchema = z
    .object({
    _id: z.string(),
    organizationId: z.string(), // TENANT KEY - REQUIRED
    propertyId: z.string(),
    uploadedBy: z.string().optional(), // User ID
    // Metadata
    title: z.string().min(1, 'Document title is required'),
    fileName: z.string().optional(),
    mimeType: z.string().default('application/pdf'),
    sizeBytes: z.number().int().positive().optional(),
    pageCount: z.number().int().positive().optional(),
    // Status and Type
    status: DocumentStatusEnum.default('UPLOADED'),
    type: DocumentTypeEnum.default('UPLOADED'),
    // Sort order for drag-drop reordering (cover page = 0, others start at 1)
    sortOrder: z.number().int().min(0).default(1),
    // S3 Pointers
    storage: DocumentStorageSchema,
    // DocuSign (populated after send)
    docusign: DocumentDocuSignSchema.optional(),
    // Stamps (receipt acknowledgment overlays)
    stamps: z.array(z.object({
        stampNumber: z.number(),
        page: z.number(),
        appliedBy: z.string(),
        appliedAt: z.coerce.date(),
        xNorm: z.number().optional(),
        yNorm: z.number().optional(),
        wNorm: z.number().optional(),
        hNorm: z.number().optional(),
    })).optional(),
    // v9.x: True when the upload landed during an in-flight INSIGHTS refresh
    // and analysis was deferred. The refresh's completion handler promotes
    // these into a fresh batch when the refresh finishes.
    pendingAnalysisAfterRefresh: z.boolean().optional(),
})
    .merge(TimestampsSchema);
/**
 * Presigned upload request
 */
export const PresignedUploadRequestSchema = z.object({
    propertyId: z.string(),
    fileName: z.string().min(1),
    mimeType: z.string().default('application/pdf'),
    sizeBytes: z.number().int().positive().optional(),
});
/**
 * Presigned upload response
 */
export const PresignedUploadResponseSchema = z.object({
    uploadUrl: z.string().url(),
    s3Key: z.string(),
    expiresIn: z.number(), // seconds
});
/**
 * Document creation input (after upload to S3)
 */
export const CreateDocumentInputSchema = z.object({
    propertyId: z.string(),
    title: z.string().min(1, 'Document title is required'),
    fileName: z.string().optional(),
    s3Key: z.string(),
    mimeType: z.string().default('application/pdf'),
    sizeBytes: z.number().int().positive().optional(),
});
/**
 * Document update input
 */
export const UpdateDocumentInputSchema = z.object({
    title: z.string().min(1).optional(),
    fileName: z.string().min(1).optional(),
    status: DocumentStatusEnum.optional(),
});
/**
 * Combine documents input
 */
export const CombineDocumentsInputSchema = z.object({
    propertyId: z.string().min(1),
    documentIds: z.array(z.string().min(1)).min(2),
    title: z.string().min(1),
});
/**
 * Split document input - extract pages into new documents
 */
export const SplitDocumentInputSchema = z.object({
    propertyId: z.string().min(1),
    documentId: z.string().min(1),
    splits: z.array(z.object({
        title: z.string().min(1),
        pages: z.array(z.number().int().positive()).min(1),
    })).min(1),
});
/**
 * Document list query parameters
 */
export const DocumentListQuerySchema = z.object({
    propertyId: z.string().optional(),
    status: DocumentStatusEnum.optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * Document source enum (v3.2 - Shadow Property Support)
 * Indicates where the document came from when viewing a shadow property
 */
export const DocumentSourceEnum = z.enum([
    'LOCAL', // Document uploaded by the current user's org (editable)
    'SHARED', // Document synced from source property (read-only)
]);
/**
 * Document response (safe for client)
 *
 * Includes optional shadow property fields that are added by the API
 * when listing documents for a shadow property (v3.2)
 */
export const DocumentResponseSchema = DocumentSchema.extend({
    // Shadow property fields (v3.2) - only present when fetching docs for a shadow property
    // For SHARED docs: source='SHARED', isReadOnly=true
    // For LOCAL docs: source='LOCAL', isReadOnly=false
    source: DocumentSourceEnum.optional(),
    isReadOnly: z.boolean().optional(),
    sourcePropertyId: z.string().optional(),
});
//# sourceMappingURL=document.js.map